/**
 * Palantir PMS - Read-only popup de consulta de requisiciones internas
 * Autor / Author: Alberto Castro Ull
 * Fecha: 12/10/2025
 * Todos los derechos reservados / All rights reserved.
 */

import { requisitionDisplayQuantity, requisitionDisplayPrice, selectedRequisitionUnit } from "../../utils/requisitionUnits";
import { isParStockPurchase, parStockCoverageLabel } from "../../utils/purchaseRequisitionSource";
import React, { useState } from "react";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import { getDocumentTranslator } from "./documentPopupMessages";
import SaveRequisitionTemplateDialog from "./SaveRequisitionTemplateDialog";
import "../../PurchasingUi.css";
import "./PurchasingDocumentPopups.css";
import useIsMobile from "../../../hooks/useIsMobile";

const popupCss = `
  .rqv-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--pu-overlay);
    backdrop-filter: blur(4px);
    padding: 16px;
  }

  .rqv-modal {
    width: min(1120px, 96vw);
    max-height: 94vh;
    overflow: auto;
    border-radius: 18px;
    border: 1px solid var(--pu-border);
    background:
      var(--pu-surface);
    box-shadow: var(--pu-shadow);
    color: var(--pu-text);
  }

  .rqv-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 18px 20px 14px;
    border-bottom: 1px solid var(--pu-border);
    background: var(--pu-soft);
  }

  .rqv-title {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 900;
    color: var(--pu-text);
  }

  .rqv-subtitle {
    margin-top: 5px;
    font-size: 0.85rem;
    color: var(--pu-muted);
  }

  .rqv-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid var(--pu-border);
    font-size: 0.72rem;
    letter-spacing: 0.2px;
    text-transform: uppercase;
    font-weight: 800;
  }

  .rqv-status--draft {
    background: var(--pu-soft);
    color: var(--pu-text);
    border-color: var(--pu-border);
  }

  .rqv-status--confirmed {
    background: var(--pu-soft);
    color: var(--pu-text);
    border-color: var(--pu-border);
  }

  .rqv-status--converted {
    background: var(--pu-success-bg);
    color: var(--pu-success);
    border-color: var(--pu-success);
  }

  .rqv-status--cancelled {
    background: var(--pu-danger-bg);
    color: var(--pu-danger);
    border-color: var(--pu-danger);
  }

  .rqv-status--other {
    background: var(--pu-soft);
    color: var(--pu-text);
    border-color: var(--pu-border);
  }

  .rqv-body {
    padding: 16px 20px 20px;
  }

  .rqv-metrics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 10px;
    margin-bottom: 14px;
  }

  .rqv-metric {
    border: 1px solid var(--pu-border);
    border-radius: 12px;
    background: var(--pu-soft);
    padding: 9px 11px;
  }

  .rqv-metricLabel {
    font-size: 0.7rem;
    color: var(--pu-muted);
    text-transform: uppercase;
    letter-spacing: 0.45px;
    font-weight: 700;
    margin-bottom: 3px;
  }

  .rqv-metricValue {
    font-size: 0.98rem;
    font-weight: 800;
    color: var(--pu-text);
  }

  .rqv-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;
  }

  .rqv-field {
    min-width: 0;
  }

  .rqv-field--3 {
    grid-column: span 3;
  }

  .rqv-field--4 {
    grid-column: span 4;
  }

  .rqv-field--6 {
    grid-column: span 6;
  }

  .rqv-label {
    display: block;
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.42px;
    color: var(--pu-muted);
    margin-bottom: 5px;
    font-weight: 700;
  }

  .rqv-input,
  .rqv-textarea {
    width: 100%;
    border-radius: 10px;
    border: 1px solid var(--pu-border);
    background: var(--pu-soft);
    color: var(--pu-text);
    padding: 9px 11px;
  }

  .rqv-input:disabled,
  .rqv-textarea:disabled {
    opacity: 1;
  }

  .rqv-textarea {
    min-height: 84px;
    resize: vertical;
  }

  .rqv-linesHeader {
    margin-bottom: 9px;
  }

  .rqv-linesTitle {
    margin: 0;
    font-size: 0.96rem;
    font-weight: 800;
    color: var(--pu-text);
  }

  .rqv-tableWrap {
    border-radius: 14px;
    border: 1px solid var(--pu-border);
    overflow: auto;
    max-height: 48vh;
    background: var(--pu-soft);
  }

  .rqv-modal,
  .rqv-tableWrap {
    scrollbar-width: thin;
    scrollbar-color: var(--pu-border) var(--pu-soft);
  }

  .rqv-modal::-webkit-scrollbar,
  .rqv-tableWrap::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }

  .rqv-modal::-webkit-scrollbar-thumb,
  .rqv-tableWrap::-webkit-scrollbar-thumb {
    background: var(--pu-surface);
    border-radius: 999px;
  }

  .rqv-modal::-webkit-scrollbar-track,
  .rqv-tableWrap::-webkit-scrollbar-track {
    background: var(--pu-surface);
    border-radius: 999px;
  }

  .rqv-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 840px;
  }

  .rqv-table thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #223a7a;
    text-align: left;
    padding: 10px 10px;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #ffffff;
    border-bottom: 1px solid var(--pu-border);
    white-space: nowrap;
  }

  .rqv-table tbody td {
    padding: 10px 10px;
    border-bottom: 1px solid var(--pu-border);
    color: var(--pu-text);
    font-size: 0.87rem;
    vertical-align: middle;
  }

  .rqv-itemName {
    font-weight: 700;
    color: var(--pu-text);
  }

  .rqv-itemMeta {
    color: var(--pu-muted);
    font-size: 0.73rem;
    margin-top: 1px;
  }

  .rqv-money {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .rqv-mobileCards {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .rqv-mobileCard {
    border: 1px solid var(--pu-border);
    background: var(--pu-soft);
    border-radius: 12px;
    padding: 10px 11px;
  }

  .rqv-mobileGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 10px;
    margin-top: 7px;
  }

  .rqv-mobileLabel {
    font-size: 0.67rem;
    color: var(--pu-muted);
    text-transform: uppercase;
    letter-spacing: 0.35px;
    margin-bottom: 1px;
    font-weight: 700;
  }

  .rqv-mobileValue {
    font-size: 0.82rem;
    color: var(--pu-text);
  }

  .rqv-empty {
    padding: 22px 14px;
    text-align: center;
    color: var(--pu-muted);
  }

  .rqv-actions {
    margin-top: 16px;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .rqv-templateNotice {
    margin-top: 16px;
    padding: 10px 12px;
    border-radius: 8px;
    color: var(--pu-success);
    background: var(--pu-success-bg);
  }

  .rqv-closeBtn {
    border-radius: 10px;
    border: 1px solid var(--pu-border);
    background: var(--pu-soft);
    color: var(--pu-text);
    padding: 9px 13px;
    font-size: 0.84rem;
    font-weight: 700;
    cursor: pointer;
  }

  @media (max-width: 980px) {
    .rqv-head {
      flex-direction: column;
      align-items: stretch;
    }

    .rqv-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .rqv-field--3,
    .rqv-field--4,
    .rqv-field--6 {
      grid-column: span 1;
    }
  }
`;

const statusToneMap = {
  draft: "draft",
  confirmed: "confirmed",
  converted: "converted",
  cancelled: "cancelled",
};

const statusLabelMap = {
  draft: "Draft",
  confirmed: "Confirmed",
  converted: "Converted",
  cancelled: "Cancelled",
};

const toStatus = (value) => String(value || "").trim().toLowerCase();

const formatDate = (value, locale) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatMoney = (value, locale) =>
  Number(value || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function ViewRequisitionPopup({ isOpen, onClose, requisition, onRepeat }) {
  if (!isOpen || !requisition) return null;
  return <RequisitionView key={requisition.id} onClose={onClose} requisition={requisition} onRepeat={onRepeat} />;
}

function RequisitionView({ onClose, requisition, onRepeat }) {
  const { themeId, languageId } = usePmsTheme();
  const t = getDocumentTranslator(languageId);
  const locale = languageId === "es" ? "es-ES" : "en-GB";
  const isMobile = useIsMobile();
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateNotice, setTemplateNotice] = useState("");
  const currentUser = readStoredUser();
  const canCreateTemplate = Number(currentUser?.id) === 1 || currentUser?.permissions?.["purchasing.requisitions.create"] === true;
  const [businessUnitId] = useState(() => requisition.business_unit_id ?? readStoredUser()?.active_business_unit_id);

  const lines = Array.isArray(requisition.lines) ? requisition.lines : [];
  const totalAmount = lines.reduce((sum, line) => {
    const quantity = Number.parseFloat(line.quantity || 0);
    const unitPrice = Number.parseFloat(line.unit_price || 0);
    return sum + quantity * unitPrice;
  }, 0);

  const suppliersCount = new Set(
    lines
      .map(
        (line) =>
          line?.supplier?.name ||
          line?.item?.item_suppliers?.[0]?.supplier?.name ||
          ""
      )
      .map((name) => String(name || "").trim())
      .filter(Boolean)
  ).size;

  const status = toStatus(requisition.status);
  const statusTone = statusToneMap[status] || "other";
  const statusLabel =
    statusLabelMap[status] || (status ? status[0].toUpperCase() + status.slice(1) : t("Unknown"));

  const resolveLine = (line) => {
    const quantity = Number.parseFloat(line.quantity || 0);
    const unitPrice = Number.parseFloat(line.unit_price || 0);
    const option = selectedRequisitionUnit(line, line.item, [], languageId);
    return {
      itemLabel: line?.item?.code
        ? `${line.item.code} - ${line.item.name || ""}`.trim()
        : line?.item?.name || t("Item #{0}", { 0: line.item_id || "-" }),
      supplierLabel:
        line?.supplier?.name ||
        line?.item?.item_suppliers?.[0]?.supplier?.name ||
        "-",
      quantity: requisitionDisplayQuantity(line, option),
      unitLabel: option?.label || "",
      unitPrice: requisitionDisplayPrice(unitPrice, option, locale),
      total: quantity * unitPrice,
      notes: line?.notes || "-",
    };
  };

  return (
    <div className="rqv-overlay pilcore-purchasing-overlay pilcore-document-popup" data-theme={themeId}>
      <style>{popupCss}</style>
      <div className="rqv-modal" role="dialog" aria-modal="true" aria-label={t("View Purchase Requisition")}>
        <div className="rqv-head">
          <div>
            <h5 className="rqv-title">{t("View Purchase Requisition")}</h5>
            <div className="rqv-subtitle">
              {requisition.requisition_code ? t("Code: {0}", { 0: requisition.requisition_code }) : "-"}
            </div>
          </div>
          <span className={`rqv-status rqv-status--${statusTone}`}>{t(statusLabel)}</span>
        </div>

        <div className="rqv-body">
          <div className="rqv-metrics">
            <div className="rqv-metric">
              <div className="rqv-metricLabel">{t("Request Date")}</div>
              <div className="rqv-metricValue">{formatDate(requisition.request_date || requisition.created_at, locale)}</div>
            </div>
            <div className="rqv-metric">
              <div className="rqv-metricLabel">{t("Expected Delivery")}</div>
              <div className="rqv-metricValue">{formatDate(requisition.expected_delivery_date, locale)}</div>
            </div>
            <div className="rqv-metric">
              <div className="rqv-metricLabel">{t("Requested By")}</div>
              <div className="rqv-metricValue">{isParStockPurchase(requisition) ? parStockCoverageLabel(languageId) : requisition.requested_by || requisition.requested_by_name || "-"}</div>
            </div>
            <div className="rqv-metric">
              <div className="rqv-metricLabel">{t("Suppliers")}</div>
              <div className="rqv-metricValue">{suppliersCount}</div>
            </div>
            <div className="rqv-metric">
              <div className="rqv-metricLabel">{t("Lines")}</div>
              <div className="rqv-metricValue">{lines.length}</div>
            </div>
            <div className="rqv-metric">
              <div className="rqv-metricLabel">{t("Total")}</div>
              <div className="rqv-metricValue">{formatMoney(totalAmount, locale)} €</div>
            </div>
          </div>

          <div className="rqv-grid">
            <div className="rqv-field rqv-field--3">
              <label className="rqv-label">{t("Requisition Code")}</label>
              <input className="rqv-input" value={requisition.requisition_code || "-"} disabled />
            </div>
            <div className="rqv-field rqv-field--3">
              <label className="rqv-label">{t("Status")}</label>
              <input className="rqv-input" value={t(statusLabel)} disabled />
            </div>
            <div className="rqv-field rqv-field--6">
              <label className="rqv-label">{t("Notes")}</label>
              <textarea className="rqv-textarea" value={requisition.notes || "-"} disabled />
            </div>
          </div>

          <div className="rqv-linesHeader">
            <h6 className="rqv-linesTitle">{t("Requisition Lines")}</h6>
          </div>

          {isMobile ? (
            <div className="rqv-mobileCards">
              {lines.length ? (
                lines.map((line, index) => {
                  const summary = resolveLine(line);
                  return (
                    <div key={line.id || `${line.item_id}-${index}`} className="rqv-mobileCard">
                      <div className="rqv-itemName">{summary.itemLabel}</div>
                      <div className="rqv-itemMeta">{summary.supplierLabel}</div>
                      <div className="rqv-mobileGrid">
                        <div>
                          <div className="rqv-mobileLabel">{t("Quantity")}</div>
                          <div className="rqv-mobileValue">{summary.quantity}{summary.unitLabel && ` ${summary.unitLabel}`}</div>
                        </div>
                        <div>
                          <div className="rqv-mobileLabel">{t("Unit Price")}</div>
                          <div className="rqv-mobileValue">{summary.unitPrice} €</div>
                        </div>
                        <div>
                          <div className="rqv-mobileLabel">{t("Line Total")}</div>
                          <div className="rqv-mobileValue">{formatMoney(summary.total, locale)} €</div>
                        </div>
                        <div>
                          <div className="rqv-mobileLabel">{t("Notes")}</div>
                          <div className="rqv-mobileValue">{summary.notes}</div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="rqv-empty">{t("No lines available.")}</div>
              )}
            </div>
          ) : (
            <div className="rqv-tableWrap">
              <table className="rqv-table">
                <thead>
                  <tr>
                    <th>{t("Item")}</th>
                    <th>{t("Supplier")}</th>
                    <th>{t("Quantity")}</th>
                    <th>{t("Unit Price (€)")}</th>
                    <th>{t("Total (€)")}</th>
                    <th>{t("Notes")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length ? (
                    lines.map((line, index) => {
                      const summary = resolveLine(line);
                      return (
                        <tr key={line.id || `${line.item_id}-${index}`}>
                          <td>
                            <div className="rqv-itemName">{summary.itemLabel}</div>
                          </td>
                          <td>{summary.supplierLabel}</td>
                          <td>{summary.quantity}{summary.unitLabel && ` ${summary.unitLabel}`}</td>
                          <td className="rqv-money">{summary.unitPrice}</td>
                          <td className="rqv-money">{formatMoney(summary.total, locale)}</td>
                          <td>{summary.notes}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="rqv-empty">{t("No lines available.")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {templateNotice && <div className="rqv-templateNotice" role="status">{templateNotice}</div>}
          <div className="rqv-actions">
            {canCreateTemplate && <button type="button" className="rqv-closeBtn" onClick={() => {
              setTemplateNotice("");
              setTemplateOpen(true);
            }}>{t("Save as a template")}</button>}
            {canCreateTemplate && onRepeat && <button type="button" className="rqv-closeBtn"
              onClick={() => onRepeat(requisition)}>{t("Repeat")}</button>}
            <button type="button" className="rqv-closeBtn" onClick={onClose}>{t("Close")}</button>
          </div>
        </div>
      </div>
      {canCreateTemplate && templateOpen && <SaveRequisitionTemplateDialog
        lines={lines} businessUnitId={businessUnitId}
        onClose={() => setTemplateOpen(false)}
        onSaved={(name) => {
          setTemplateOpen(false);
          setTemplateNotice(t('Template "{0}" saved.', { 0: name }));
        }} />}
    </div>
  );
}
